// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type ComponentType,
  type FC,
  memo as memoize,
  type PropsWithChildren,
} from 'react';

export interface DefineComponentProps<
  TDisplayName extends Capitalize<string>,
  TProps extends Record<string, any> = {},
  TSetupResult extends Record<string, any> = {},
  TSlots extends Record<Capitalize<string>, ComponentType<any>> = {},
> {
  displayName: TDisplayName;
  useSetup?: (props: TProps) => TSetupResult;
  Slots?: TSlots;
  View: FC<TProps & TSetupResult & SlotsProps<TSlots> & PropsWithChildren>;
  memo?: boolean;
  memoOptions?: (
    prevProps: Readonly<TProps>,
    nextProps: Readonly<TProps>,
  ) => boolean;
}

export interface SlotsProps<
  TSlots extends Record<Capitalize<string>, ComponentType<any>> = {},
> {
  Slots: TSlots;
}

export const defineComponent = <
  TDisplayName extends Capitalize<string>,
  TProps extends Record<string, any> = {},
  TSetupResult extends Record<string, any> = {},
  TSlots extends Record<Capitalize<string>, ComponentType<any>> = {},
>({
  displayName,
  useSetup = () => ({} as TSetupResult),
  View,
  Slots = {} as TSlots,
  memo: shouldMemoize,
  memoOptions,
}: DefineComponentProps<TDisplayName, TProps, TSetupResult, TSlots>): ComponentType<
  TProps & PropsWithChildren
> => {
  const Component: FC<TProps & PropsWithChildren> = ({
    children,
    ...props
  }: TProps & PropsWithChildren) => {
    const data = useSetup({ ...(props as TProps), children } as TProps);

    return (
      <View {...(props as TProps)} {...data} Slots={Slots} children={children} />
    );
  };

  const MemoizedComponent = shouldMemoize
    ? memoize(Component, memoOptions)
    : Component;

  MemoizedComponent.displayName = displayName;

  return MemoizedComponent;
};
